import type {
  PageTemplateSuggestion,
  PageTemplateSuggestionsResponse
} from '../types';

export type PageMode = 'auto' | 'manual';

interface ResolveInput {
  mode: PageMode;
  suggestions: PageTemplateSuggestion[] | undefined;
  /** Suggestion the operator picked in auto mode (defaults to rank 1). */
  autoChoice?: number;
  /** Template picked from the full catalog in manual mode. */
  manualPick?: number;
  /** The container form's configured template; null = none. */
  defaultTemplateId: number | null;
}

/**
 * The template a page will be extracted with. Auto takes the operator's
 * chosen suggestion (if it is still in the ranked list) else rank 1; manual
 * takes the catalog pick. Both fall back to the container default.
 */
export function resolvePageTemplate({
  mode,
  suggestions,
  autoChoice,
  manualPick,
  defaultTemplateId
}: ResolveInput): number | null {
  if (mode === 'manual') return manualPick ?? defaultTemplateId;

  const list = suggestions ?? [];
  if (autoChoice != null && list.some((s) => s.template_id === autoChoice)) {
    return autoChoice;
  }
  return list[0]?.template_id ?? defaultTemplateId;
}

/** Pages with a ranking open in auto; pages without one go straight to manual. */
export function initialPageMode(
  suggestions: PageTemplateSuggestion[] | undefined
): PageMode {
  return suggestions && suggestions.length > 0 ? 'auto' : 'manual';
}

export type PageSuggestionsState = 'idle' | 'pending' | 'ready' | 'failed';

interface SuggestionsStateInput {
  /** The operator asked for suggestions (ranking is opt-in, never on open). */
  requested: boolean;
  /** Backend payload status; undefined = first fetch still in flight. */
  status: 'pending' | 'ready' | undefined;
  /** Pages returned by the suggestions payload. */
  pageCount: number;
  isError: boolean;
  /** The poll loop gave up while the payload was still pending. */
  pollTimedOut: boolean;
}

/**
 * Where the per-page ranking stands. Only 'pending' should spin. The backend
 * caches a failed ranking as a 'ready' payload with no pages (a successful
 * ranking always carries its pages, even when nothing matched), so that, a
 * request error, and a poll window that ran out are all terminal failures.
 * Before the operator asks, the state is 'idle' whatever the query holds.
 */
export function pageSuggestionsState({
  requested,
  status,
  pageCount,
  isError,
  pollTimedOut
}: SuggestionsStateInput): PageSuggestionsState {
  if (!requested) return 'idle';
  if (isError) return 'failed';
  if (status === 'ready') return pageCount === 0 ? 'failed' : 'ready';
  return pollTimedOut ? 'failed' : 'pending';
}

/** One page's own on-demand ranking ("تلقائي" on that row). */
export type PageRequestInput =
  | { requested: false }
  | {
      requested: true;
      status: 'pending' | 'ready' | undefined;
      /** The backend cached this page's call as failed. */
      failed: boolean;
      suggestions: PageTemplateSuggestion[];
      isError: boolean;
      pollTimedOut: boolean;
    };

interface ResolvePageSuggestionsInput {
  containerState: PageSuggestionsState;
  /** The container ranking's list for this page (when ready). */
  containerSuggestions: PageTemplateSuggestion[] | undefined;
  page: PageRequestInput;
}

/**
 * Where one page's suggestions come from. A non-empty container list wins
 * (it already reuses page rankings server-side); otherwise the page's own
 * request decides; otherwise the container state does. Mirrors
 * pageSuggestionsState's failure rules for the page request.
 */
export function resolvePageSuggestions({
  containerState,
  containerSuggestions,
  page
}: ResolvePageSuggestionsInput): {
  state: PageSuggestionsState;
  suggestions: PageTemplateSuggestion[];
} {
  if (containerState === 'ready' && containerSuggestions?.length) {
    return { state: 'ready', suggestions: containerSuggestions };
  }

  if (page.requested) {
    if (page.isError || (page.status === 'ready' && page.failed)) {
      return { state: 'failed', suggestions: [] };
    }
    if (page.status === 'ready') {
      return { state: 'ready', suggestions: page.suggestions };
    }
    return { state: page.pollTimedOut ? 'failed' : 'pending', suggestions: [] };
  }

  if (containerState === 'ready') {
    return { state: 'ready', suggestions: containerSuggestions ?? [] };
  }
  return { state: containerState, suggestions: [] };
}

/**
 * What the dialog restores on open from the `cached_only` read. A ranking is
 * paid for once and cached server-side for a day, but the "requested" flag
 * lived in component state, so a reload forced the operator to rank (and pay)
 * again. A cached container ranking reads as already requested; pages ranked
 * one at a time ("Auto" on a row) have their single-page queries re-attached.
 * `dropPages` is set once a regenerate cleared those entries — re-attaching
 * them would queue a duplicate job per page beside the container one.
 */
export function restoredSuggestions(
  payload: PageTemplateSuggestionsResponse | undefined,
  options: { dropPages?: boolean } = {}
): { rankingCached: boolean; pageIds: number[] } {
  return {
    rankingCached: payload?.status === 'ready',
    pageIds: options.dropPages ? [] : (payload?.cached_page_ids ?? [])
  };
}
